import {
  mermaidConfig,
  reconstructNestedMarkdown,
  shikiTheme,
  streamdownPlugins,
  streamdownRehypePlugins,
  type MarkdownComponents,
} from '@heidi/answer-kit';
import { Banner, BannerContent, BannerTitle } from '@heidi/design-system/banner';
import { cn } from '@heidi/utils';
import { useMemo, type ComponentType, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Streamdown } from 'streamdown';

interface SafetyCalloutProps {
  children?: ReactNode;
  className?: string;
  isStreaming?: boolean;
  /** `<cite-badge>` renderer supplied by the surface that owns the citation map. */
  citeBadgeComponent?: ComponentType<{ 'data-idx'?: string }>;
}

/**
 * SafetyCallout — a prominent, warning-toned container for high-risk clinical
 * safety information in chat answers (EV-023 / QA-173 risk control).
 *
 * The LLM wraps critical safety content (black-box warnings, drug interactions,
 * dosage cautions — medications first) in `<safetycallout>…</safetycallout>`.
 * The tag is generic on purpose: any high-risk caution can go in it, and it
 * always renders in the same amber warning style so clinicians spot it without
 * reading the whole answer.
 *
 * Implementation notes:
 *   - The element name must NOT contain hyphens. Streamdown's block parser uses
 *     a `\w+` regex that doesn't match hyphens, which would split the content
 *     across blocks (same constraint as <keypoints>, EVI-509).
 *   - Children (markdown) are re-parsed with a nested Streamdown so bullet
 *     points and inline `cite-badge` markers render correctly inside the banner.
 *   - Graceful degradation: clients that don't allowlist the tag strip the
 *     wrapper but keep the inner text, so the safety content still shows (just
 *     unstyled). The backend gates emission per client via `X-Client-Id`.
 *
 * @example LLM Output:
 * ```
 * <safetycallout>
 * **Black box warning:** increased mortality in elderly patients with
 * dementia-related psychosis.
 * </safetycallout>
 * ```
 */
export const SafetyCallout = ({
  children,
  className,
  isStreaming = false,
  citeBadgeComponent,
}: SafetyCalloutProps): JSX.Element | null => {
  // Re-parse children as markdown in a nested Streamdown, preserving inline
  // cite-badge markers (shared with KeyPointsBlock — see the helper for why).
  const textContent = useMemo(() => reconstructNestedMarkdown(children), [children]);

  const components = useMemo(() => {
    const base: Record<string, unknown> = {};

    if (citeBadgeComponent) {
      base['cite-badge'] = citeBadgeComponent;
    }

    return base as MarkdownComponents;
  }, [citeBadgeComponent]);

  // Nothing to warn about — render nothing rather than an empty banner.
  if (!textContent) return null;

  return (
    <Banner
      subVariant="warning"
      data-testid="safety-callout"
      className={cn('my-4 first:mt-0', className)}
    >
      <BannerContent>
        <BannerTitle>
          <FormattedMessage
            defaultMessage="Important safety information"
            id="SOwEKAcqP6"
            description="Title of the high-risk clinical safety callout in Evidence answers"
          />
        </BannerTitle>
        {/* `dir="auto"` — content is unlocalized LLM markdown; resolve bidi
            direction from the text itself instead of inheriting the RTL
            locale direction. This surface renders Streamdown's default
            elements (no per-block component overrides to hook), so the
            wrapper carries the attribute; callout copy is a single short
            warning so whole-block resolution is fine. */}
        <div dir="auto" className="text-text-secondary [&_p+p]:mt-2 [&_p]:m-0">
          <Streamdown
            components={components}
            isAnimating={isStreaming}
            codeBlockMaxHeight={0}
            tableMaxHeight={0}
            plugins={streamdownPlugins}
            mermaid={mermaidConfig}
            shikiTheme={shikiTheme}
            rehypePlugins={streamdownRehypePlugins}
          >
            {textContent}
          </Streamdown>
        </div>
        {/* Keep the AI-generated provenance + verify-independently note inside
            the callout: the banner is intentionally authoritative, so we must
            signal the content is decision support to be independently reviewed,
            not autonomous guidance (EV-023 / non-device CDS framing). */}
        <p className="mt-1 text-xs italic text-text-tertiary">
          <FormattedMessage
            defaultMessage="AI-generated — verify against the cited source and apply your clinical judgement."
            id="5u6b/ChD+s"
            description="Disclaimer that safety-callout content is decision support, not autonomous guidance"
          />
        </p>
      </BannerContent>
    </Banner>
  );
};

export default SafetyCallout;
